import { AccountRoles } from 'common/api/users/service';
import { Routes } from 'routes';
export interface ISection<T> {
    title: string;
    id?: string;
    entries: T[];
}
import { FeaturesState } from 'common/features/featuresReducer';
import { selectCoordinatorMetaData } from 'common/features/store/duck/coordinator/duck';
import { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';

export interface ISectionEntries extends Pick<ISection<void>, 'title' | 'id'> {
    description: string;
    link: string;
    forScripWare: boolean;
    hideForRoles?: AccountRoles[];
    checkNotifications?: (s: FeaturesState) => boolean;
    featureFlags?: string[];
    internalLink?: boolean;
}

// NOTE: Do not change these ids unless explicitly told so in a ticket.
// NOTE: These ids are utilized for appcues to select specific regions to implement their tutorial
// walkthrough of the site.  This specifically targets the Coordinator-Dashboard page.
export const appcuesSelectors = {
    ordersSection: 'coord_dash_orders',
    reportsSection: 'coord_dash_reports',
    participantSection: 'coord_dash_participants',
    inviteFamilySubSection: 'coord_dash_invite_participants',
    organizationSection: 'coord_dash_organization',
    paymentsSection: 'coord_dash_payments',
    resourcesSection: 'coord_dash_resources',
    inventoryMngmntSection: 'coord_dash_inventory_mgt',
    sideMenu: 'coord_dash_org_info',
};

export const commonSections: ISection<ISectionEntries>[] = [
    {
        title: 'Orders',
        id: appcuesSelectors.ordersSection,
        entries: [
            {
                forScripWare: false,
                title: 'Enter Orders',
                description:
                    'Place physical gift card orders for inventory or on behalf of a family.',
                link: `/c/Org/Order/ProxyOrder`,
            },
            {
                forScripWare: false,
                title: 'Order Queue',
                description: 'See all open orders for your program.',
                link: `/c/Org/Order/OrderQueue`,
                checkNotifications: (state: FeaturesState) => {
                    return selectCoordinatorMetaData(state).pendingOrders > 0;
                },
            },
            {
                forScripWare: false,
                title: 'PO Manager',
                description:
                    'Submit organization purchase orders, and access shipment tracking info.',
                link: `/c/Org/Order/POManager`,
                hideForRoles: [AccountRoles.ASSISTANT_COORDINATOR],
                checkNotifications: (state: FeaturesState) => {
                    return selectCoordinatorMetaData(state).pendingPurchaseOrderCount > 0;
                },
            },
            {
                forScripWare: false,
                title: 'Family Order Form',
                description: 'Create a custom order form for families that don’t order online.',
                link: `/c/Org/Order/FamilyOrder`,
            },
        ],
    },
    {
        title: 'Reports',
        id: appcuesSelectors.reportsSection,
        entries: [
            {
                title: 'Order History',
                description: 'View your past orders based on date range, supplier, and order type.',
                link: `/c/Org/Order/POManager`,
                forScripWare: true,
            },
            {
                title: 'Packing Slips',
                description:
                    'Printable report to include in your gift card distribution to families.',
                link: `/c/Org/Manage/Report/Selection?rpt=FamilyOrderPackingSlipRebate`,
                forScripWare: false,
            },
            {
                title: 'All Reports',
                description:
                    'Run customizable reports for order history, family earnings, and more.',
                link: `/c/Org/Manage/Report`,
                forScripWare: false,
            },
        ],
    },
    {
        title: 'Families',
        id: appcuesSelectors.participantSection,
        entries: [
            {
                forScripWare: false,
                title: 'Family Accounts',
                description: 'View and manage all families in your program.',
                link: `/c/Org/Manage/FamilyAccount`,
            },
            {
                forScripWare: true,
                title: 'Invite Families',
                id: appcuesSelectors.inviteFamilySubSection,
                description: 'Use this email invitation for new families to easily sign up.',
                link: `/c/Org/Manage/FamilyInvite`,
            },
        ],
    },
    {
        title: 'Organization',
        id: appcuesSelectors.organizationSection,
        entries: [
            {
                forScripWare: true,
                title: 'Organization Profile',
                description: 'View and update your organization’s contact and payment information.',
                link: `/c/Org/Manage/OrgProfile`,
            },
            {
                forScripWare: true,
                title: 'Organization Settings',
                description:
                    "Adjust the organization's options for ordering, shipping, and inviting.",
                link: Routes.ShopAccountSettingsCoordinatorOrganizationSettings,
                featureFlags: [
                    FeatureFlagTypes.coordinator_toggles,
                    FeatureFlagTypes.enable_invite_friends_family,
                ],
                internalLink: true,
            },
            {
                forScripWare: true,
                title: 'Coordinator Roles',
                description:
                    'Change your coordinator or assign assistants to help you run your program.',
                link: `/c/Org/Manage/Role`,
                hideForRoles: [AccountRoles.ASSISTANT_COORDINATOR],
            },
            {
                title: 'Organization PIN Reset',
                description: 'Update your security PIN.',
                link: `/c/Org/Manage/OrgSecurity/PinResetVerify`,
                forScripWare: true,
            },
            {
                forScripWare: false,
                title: 'Local Products',
                description: 'Add gift card product information for Local Suppliers.',
                link: `/c/Org/Manage/Product`,
            },
            {
                forScripWare: false,
                title: 'Local Suppliers',
                description: 'Set up a local retailer/brand to add to your program.',
                link: `/c/Org/Manage/Supplier`,
            },
            {
                forScripWare: false,
                title: 'Organization Email Notifications',
                description: 'Manage email alerts for organization orders.',
                link: `/c/Org/Manage/OrgNotification`,
            },
        ],
    },
    {
        title: 'Payments',
        id: appcuesSelectors.paymentsSection,
        entries: [
            {
                forScripWare: true,
                title: 'Payment Change Form',
                description: 'Update your organizations’ online banking account information.',
                link: `/c/Org/Manage/PaymentChange`,
                hideForRoles: [AccountRoles.ASSISTANT_COORDINATOR],
            },
            {
                forScripWare: true,
                title: 'Payment Types',
                description: 'View and manage how you allow families to pay.',
                link: `/c/Org/Manage/PrestoPay`,
                hideForRoles: [AccountRoles.ASSISTANT_COORDINATOR],
            },
        ],
    },
    {
        title: 'Resources',
        id: appcuesSelectors.resourcesSection,
        entries: [
            {
                forScripWare: true,
                title: 'Coordinator Resource Center',
                description:
                    'Your one-stop resource for program news, promos, and marketing materials.',
                link: `/resources/organization/`,
            },
        ],
    },
    {
        title: 'Inventory Management & Custom Rebates',
        id: appcuesSelectors.inventoryMngmntSection,
        entries: [
            {
                forScripWare: false,
                title: 'Scripware',
                description:
                    'Add this service to manage gift card inventory and customize family earnings.',
                link: Routes.Scripware,
                hideForRoles: [AccountRoles.ASSISTANT_COORDINATOR],
            },
        ],
    },
];
